import { useCallback, useContext, useState } from 'react';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { useInvalidateWaitingRoom } from 'router/subrouters/DataRoom/hooks/useWaitingRoom';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';

export default function useMemberRequestAccess() {
  const [isLoading, setIsLoading] = useState(false);

  const { popServerError } = useContext(PopNotificationsContext);
  const { dataRoom } = useContext(DataRoomContext);
  const { setSellSideMembers, setBuySideGroups, updateMember } =
    useContext(MembersContext);

  const invalidateWaitingRoom = useInvalidateWaitingRoom(dataRoom?._id);

  const removeMember = useCallback(
    (memberId: string) => {
      setSellSideMembers((old) => old.filter(({ _id }) => _id !== memberId));

      // Buy-side members are listed through their group, so dropping them from
      // the flat list alone would leave the row on screen until a refetch.
      setBuySideGroups((old) =>
        old.map((group) => ({
          ...group,
          dataRoomMembers: group.dataRoomMembers.filter(
            ({ _id }) => _id !== memberId,
          ),
        })),
      );
    },
    [setBuySideGroups, setSellSideMembers],
  );

  const acceptRequest = useCallback(
    async (
      member: Partial<DataRoomMember>,
      onSuccess?: (member: DataRoomMember) => void,
    ) => {
      const { _id, role, qaRole } = member;

      try {
        setIsLoading(true);

        const { data } = await dataRoomApi.members.acceptRequest(_id, {
          role,
          qaRole,
        });

        invalidateWaitingRoom();
        updateMember(data);
        onSuccess?.(data);
      } catch (error) {
        popServerError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [invalidateWaitingRoom, popServerError, updateMember],
  );

  const rejectRequest = useCallback(
    async (memberId: string, onSuccess?: () => void) => {
      try {
        setIsLoading(true);

        await dataRoomApi.members.declineRequest(memberId);

        invalidateWaitingRoom();
        removeMember(memberId);
        onSuccess?.();
      } catch (error) {
        popServerError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [invalidateWaitingRoom, popServerError, removeMember],
  );

  return { isLoading, acceptRequest, rejectRequest };
}
